import React, { useEffect, useState } from "react";
import MobileBurgerMenu from "./MobileBurgerMenu";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { CircularProgress } from "@mui/material";
import DropDownMyAccountMenu from "../Media/DropDownMyAccountMenu";
import { message } from "antd";
import { Search } from "lucide-react";

export default function UserSignedIn() {
  const { currentUserInformation } = useAuth();
  const navigate = useNavigate();

  // UI state
  const [isScrolled, setIsScrolled] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategories, setSelectedCategories] = useState(new Set());

  // Optional: track width to decide when to show MobileBurgerMenu
  const [width, setWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1024
  );
  useEffect(() => {
    const onResize = () => setWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  const isMobile = width <= 768;

  // Scroll shrink
  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 150);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Quick search suggestions
  const quickSearches = [
    "Gastritis-Friendly",
    "IBS-Friendly",
    "GERD-Friendly",
    "Barretts-Esophagus-Friendly",
  ];

  const toggleCategory = (category) => {
    setSelectedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(category)) next.delete(category);
      else next.add(category);
      return next;
    });
  };

  const searchRecipe = (e) => {
    e.preventDefault();
    const q = searchQuery.trim();
    if (q === "" && selectedCategories.size === 0) {
      message.info("Enter a keyword or pick a category to search");
      return;
    }
    const params = new URLSearchParams();
    if (q) params.set("search_query", q);
    if (selectedCategories.size > 0)
      params.set("category", Array.from(selectedCategories).join(","));
    params.set("calorieTotal", "0 TO 50000");
    navigate(`/search?${params.toString()}`);
  };

  // Spacer to prevent content hiding behind fixed nav
  const spacerClass = isScrolled
    ? "h-16 sm:h-18 md:h-20"
    : "h-20 sm:h-22 md:h-24";

  return (
    <>
      <nav
        className={[
          "fixed inset-x-0 top-0 z-50 border-b border-slate-200/80 bg-white/90 backdrop-blur transition-shadow duration-300",
          isScrolled ? "shadow-md" : "shadow-sm",
          "pt-[max(env(safe-area-inset-top),0px)]",
        ].join(" ")}
        aria-label="User Navigation"
      >
        {/* Top utility (hidden on mobile) */}
        <div className="hidden md:block bg-slate-50">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 h-3 flex items-center justify-between">
            <div className="text-xs text-slate-500 font-medium" />
            <div className="flex items-center gap-3" />
          </div>
        </div>

        {/* Main row */}
        <div className="transition-all duration-300">
          <div
            className={[
              "mx-auto max-w-7xl px-4 sm:px-6 lg:px-8",
              isScrolled ? "py-2.5" : "py-4",
            ].join(" ")}
          >
            <div className="grid grid-cols-[auto_1fr_auto] items-center gap-4 sm:gap-6">
              {/* Left: burger + logo (mobile swaps to MobileBurgerMenu) */}
              <div className="flex items-center gap-3">
                <div className="hidden md:block">
                  <button
                    type="button"
                    onClick={() => navigate("/")}
                    className="group flex items-center gap-2"
                    aria-label="Go to homepage"
                  >
                    <div className="text-left">
                      <h1
                        className={[
                          "font-bold tracking-tight text-rose-500 transition-all",
                          isScrolled
                            ? "text-lg leading-5"
                            : "text-2xl leading-7",
                        ].join(" ")}
                      >
                        Gentle Gourmet
                      </h1>
                      <p
                        className={[
                          "text-slate-500 transition-all",
                          isScrolled ? "text-[11px]" : "text-xs",
                        ].join(" ")}
                      >
                        Recipes for Every Kitchen
                      </p>
                    </div>
                  </button>
                </div>

                {/* Mobile: full menu trigger/list */}
                <div className="md:hidden">
                  <MobileBurgerMenu />
                </div>
              </div>

              {/* Center: search */}
              <div className="w-full max-w-xl justify-self-center">
                <form onSubmit={searchRecipe} className="mb-1">
                  <div
                    className={[
                      "relative flex items-center rounded-full border-2 transition-all",
                      "bg-slate-50 border-slate-200 focus-within:bg-white",
                      "focus-within:border-blue-500 focus-within:ring-4 focus-within:ring-blue-500/10",
                      isScrolled ? "h-6" : "h-8",
                    ].join(" ")}
                  >
                    <Search
                      className="absolute left-3 text-slate-400"
                      size={18}
                    />
                    <input
                      type="search"
                      placeholder="Search GERD, IBS, gastritis recipes…"
                      className={[
                        "w-full bg-transparent outline-none rounded-full",
                        "pl-10 pr-32 text-[15px] placeholder:text-slate-400",
                        isScrolled ? "h-6" : "h-8",
                      ].join(" ")}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      aria-label="Search recipes"
                    />
                    <button
                      type="submit"
                      className={[
                        "absolute right-1 rounded-full px-3 font-medium text-white",
                        "bg-blue-600 hover:bg-blue-700 active:bg-blue-800 transition-colors",
                        isScrolled ? "h-6 text-xs" : "h-6 text-sm",
                      ].join(" ")}
                    >
                      Search
                    </button>
                  </div>
                </form>

                {/* Quick tags (limit on mobile for cleanliness) */}
                <div
                  className={[
                    "flex flex-wrap items-center gap-2",
                    isScrolled ? "mt-0.5" : "mt-1.5",
                  ].join(" ")}
                >
                  <span className="text-[11px] font-medium text-slate-500">
                    Popular:
                  </span>
                  {quickSearches
                    .slice(0, 2)
                    .map((s) => ({ s, showOnSm: true }))
                    .concat(
                      quickSearches
                        .slice(2)
                        .map((s) => ({ s, showOnSm: false }))
                    )
                    .map(({ s, showOnSm }) => {
                      const selected = selectedCategories.has(s);
                      return (
                        <button
                          key={s}
                          type="button"
                          onClick={() => toggleCategory(s)}
                          className={[
                            "rounded-xl border transition-all",
                            "px-3 py-1",
                            "text-xs sm:text-[12px]",
                            selected
                              ? "border-blue-600 bg-blue-600 text-white hover:bg-blue-700"
                              : "border-slate-200 text-blue-600 hover:bg-blue-600 hover:text-white",
                            isScrolled ? "py-0.5" : "",
                            showOnSm ? "" : "hidden sm:inline-flex",
                          ].join(" ")}
                        >
                          {s}
                        </button>
                      );
                    })}
                </div>
              </div>

              {/* Right: account + Patreon */}
              <div className="justify-self-end flex items-center gap-3">
                <div className="min-w-[44px] min-h-[24px] flex items-center justify-center">
                  {currentUserInformation !== undefined ? (
                    <DropDownMyAccountMenu profile={currentUserInformation} />
                  ) : (
                    <CircularProgress size={20} />
                  )}
                </div>

                <a
                  href="https://patreon.com/GentleGourmetRecipes?utm_medium=unknown&utm_source=join_link&utm_campaign=creatorshare_creator&utm_content=copyLink"
                  target="_blank"
                  rel="noreferrer"
                  className="rounded-full bg-amber-500 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-amber-600 active:bg-amber-700"
                >
                  Patreon
                </a>
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Spacer to prevent content being hidden behind fixed navbar */}
      <div aria-hidden className={spacerClass} />
    </>
  );
}
